import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, ShoppingBag, User, Globe, Moon, Sun, Menu, X, LogOut, HelpCircle, Store } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { useApp } from "@/contexts/AppContext";
import CartDrawer from "@/components/storefront/CartDrawer";
import BrandLogo from "@/components/BrandLogo";

export default function Navbar() {
  const { t, lang, toggleLang, theme, toggleTheme, cartCount, user, logout } = useApp();
  const [search, setSearch] = useState("");
  const [mobileOpen, setMobileOpen] = useState(false);
  const [cartOpen, setCartOpen] = useState(false);
  const navigate = useNavigate();

  const onSearch = (e) => {
    e.preventDefault();
    if (search.trim()) navigate(`/?q=${encodeURIComponent(search)}`);
  };

  return (
    <header className="sticky top-0 z-40 border-b border-border bg-surface/85 backdrop-blur-md print-hide">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center gap-4">
        <Link to="/" data-testid="navbar-brand" className="flex items-center gap-2 me-2 group">
          <BrandLogo size={38} />
          <span className="text-base sm:text-lg font-bold tracking-tight hidden sm:inline">
            {t.brand}
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-1 ms-4">
          <Link to="/" className="nav-pill" data-testid="nav-home">{t.breadcrumbHome}</Link>
          <Link to="/?cat=all" className="nav-pill" data-testid="nav-all-products">{t.allProducts}</Link>
          <Link to="/courses" className="nav-pill" data-testid="nav-courses">{t.courses}</Link>
          <Link to="/my-orders" className="nav-pill" data-testid="nav-my-orders">{lang === "ar" ? "طلباتي" : "My orders"}</Link>
        </nav>

        <form onSubmit={onSearch} className="hidden md:flex flex-1 max-w-md relative ms-auto">
          <Search className="absolute start-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
          <Input
            data-testid="navbar-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t.search}
            className="ps-9 bg-background border-border"
          />
        </form>

        <div className="flex items-center gap-1 md:ms-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={toggleLang}
            data-testid="language-switcher"
            className="gap-1.5"
          >
            <Globe className="h-4 w-4" />
            <span className="hidden sm:inline text-sm">{t.language}</span>
          </Button>

          <Button
            variant="ghost"
            size="icon"
            onClick={() => setCartOpen(true)}
            data-testid="cart-button"
            className="relative"
            aria-label={t.cart}
          >
            <ShoppingBag className="h-5 w-5" />
            {cartCount > 0 && (
              <span
                data-testid="cart-count-badge"
                className="absolute -top-0.5 -end-0.5 min-w-5 h-5 px-1 rounded-full bg-[#FFD60A] text-black text-[10px] font-bold flex items-center justify-center"
              >
                {cartCount}
              </span>
            )}
          </Button>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                data-testid="profile-button"
                aria-label={t.profile}
              >
                <User className="h-5 w-5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel className="text-xs uppercase tracking-widest text-muted-foreground">
                {user ? user.name : t.profile}
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem data-testid="settings-account" className="gap-2">
                <User className="h-4 w-4" />
                {t.account}
              </DropdownMenuItem>
              {user?.role === "admin" && (
                <DropdownMenuItem
                  data-testid="settings-store"
                  className="gap-2"
                  onClick={() => navigate("/admin")}
                >
                  <Store className="h-4 w-4" />
                  {t.store}
                </DropdownMenuItem>
              )}
              <DropdownMenuItem data-testid="settings-help" className="gap-2">
                <HelpCircle className="h-4 w-4" />
                {t.help}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <div
                data-testid="settings-darkmode-row"
                className="flex items-center justify-between px-2 py-1.5 text-sm"
              >
                <span className="flex items-center gap-2">
                  {theme === "dark" ? <Moon className="h-4 w-4" /> : <Sun className="h-4 w-4" />}
                  {t.darkMode}
                </span>
                <Switch
                  data-testid="darkmode-toggle"
                  checked={theme === "dark"}
                  onCheckedChange={toggleTheme}
                />
              </div>
              <DropdownMenuSeparator />
              {user ? (
                <DropdownMenuItem
                  data-testid="logout-button"
                  onClick={logout}
                  className="gap-2 text-destructive focus:text-destructive"
                >
                  <LogOut className="h-4 w-4" />
                  {t.signOut}
                </DropdownMenuItem>
              ) : (
                <DropdownMenuItem
                  data-testid="signin-link"
                  onClick={() => navigate("/login")}
                  className="gap-2"
                >
                  <User className="h-4 w-4" />
                  {t.signIn}
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>

          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden"
            onClick={() => setMobileOpen((o) => !o)}
            aria-label="menu"
            data-testid="mobile-menu-toggle"
          >
            {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </Button>
        </div>
      </div>
      {mobileOpen && (
        <div className="lg:hidden border-t border-border px-4 py-3 bg-surface space-y-3">
          <form onSubmit={onSearch} className="relative">
            <Search className="absolute start-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t.search}
              className="ps-9"
            />
          </form>
          <div className="flex gap-2">
            <Link to="/" className="nav-pill" onClick={() => setMobileOpen(false)}>{t.breadcrumbHome}</Link>
            <Link to="/?cat=all" className="nav-pill" onClick={() => setMobileOpen(false)}>{t.allProducts}</Link>
          </div>
        </div>
      )}

      <CartDrawer open={cartOpen} onOpenChange={setCartOpen} />
    </header>
  );
}
